import config from "flow/config";
import type MyFlow from "flow/MyFlow";
import { Dictionary } from "flow/api/types";

let myFlow: MyFlow;

export type ValidEnvTypes = string | boolean | number;

class KeyValuePair {
    key: string;
    value: ValidEnvTypes;
}

const defaultEnv: Dictionary<ValidEnvTypes> = {
    console: false,
    crossLaunch: false,
    culture: "en-GB",
    debug: false,
    debugEvents: false,
    debugInactivity: false,
    debugOrder: false,
    discoveryAjaxTimeout: 60000,
    environmentBanner: "",
    genericAjaxTimeout: 600000,
    heartbeatInterval: 5 * 60 * 1000, // 5 minutes
    heartbeatTimeout: 60000,
    homeConfiguration: false,
    ignoreBootErrors: false,
    ignorePageUnloadDirtyCheck: false,
    inactivityDialogTimeout: 0,
    inactivityTimeout: 0,
    inspector: false,
    productionMode: true,
    selectedTheme: "",
    sessionRecovery: false,
    showProductSelector: true,
    singleModule: null,
    systemInformation: false,
    systemVersion: null,
    themeSelector: false,
    useProductSelector: false,
    userId: "",
    useSignalR: false,
    uiCulture: "en-GB"
};

let env: { [id: string]: ValidEnvTypes } = { ...defaultEnv };

function setup(mFlow: MyFlow): void {
    myFlow = mFlow;
}

const get = <T = ValidEnvTypes>(key: string) => env[key] as T;

const set = (key: string, value: ValidEnvTypes): void => {
    env[key] = value;
    myFlow.publish(config.topics.environmentPrefix + key, value);
    myFlow.debug("Env " + key + " = ", value);
};

const userSet = (key: string, value: string | boolean | number): void => {
    if (instructions[key]) {
        throw Error(key + " - " + instructions[key]);
    }
    set(key, value);
};

const isEnabled = (key: string) => env[key] === true;

const list = (): KeyValuePair[] => {
    const variables = Object.entries(env).map(([key, value]) => {
        return {
            key: key,
            value: value
        } as KeyValuePair;
    });

    const uiCulture = get(keys.uiCulture) as string;
    variables.sort((a: KeyValuePair, b: KeyValuePair) => a.key.localeCompare(b.key, uiCulture));

    return variables;
};

function reset(): void {
    env = { ...defaultEnv };
}

const keys = {
    crossLaunch: "crossLaunch",
    culture: "culture",
    debug: "debug",
    debugEvents: "debugEvents",
    debugInactivity: "debugInactivity",
    debugOrder: "debugOrder",
    description: "description",
    discoveryAjaxTimeout: "discoveryAjaxTimeout",
    environmentBanner: "environmentBanner",
    environmentDescription: "environmentDescription",
    genericAjaxTimeout: "genericAjaxTimeout",
    heartbeatInterval: "heartbeatInterval",
    heartbeatTimeout: "heartbeatTimeout",
    homeConfiguration: "homeConfiguration",
    ignoreBootErrors: "ignoreBootErrors",
    ignorePageUnloadDirtyCheck: "ignorePageUnloadDirtyCheck",
    impersonating: "impersonating",
    inactivityDialogTimeout: "inactivityDialogTimeout",
    inactivityDialogTimeoutUnsaved: "inactivityDialogTimeoutUnsaved",
    inactivityTimeout: "inactivityTimeout",
    inspector: "inspector",
    logoutUrl: "logoutUrl",
    moduleSelector: "moduleSelector",
    productId: "productId",
    productIdRemember: "productIdRemember",
    productionMode: "productionMode",
    productName: "productName",
    productPayload: "productPayload",
    productPayloadRemember: "productPayloadRemember",
    runtimeContext: "runtimeContext",
    selectedTheme: "selectedTheme",
    sessionRecovery: "sessionRecovery",
    showProductSelector: "showProductSelector",
    singleModule: "singleModule",
    systemInformation: "systemInformation",
    systemVersion: "systemVersion",
    themeSelector: "themeSelector",
    uiCulture: "uiCulture",
    useProductSelector: "useProductSelector",
    userId: "userId",
    userName: "userName",
    useSignalR: "useSignalR",
    version: "version"
};

const instructions: Dictionary = {
    crossLaunch: "readonly",
    culture: "readonly",
    ecma: "readonly",
    heartbeatInterval: "readonly",
    ignoreBootErrors: "readonly",
    inactivityDialogTimeout: "readonly",
    inactivityDialogTimeoutUnsaved: "readonly",
    inactivityTimeout: "readonly",
    logoutUrl: "readonly",
    productionMode: "readonly",
    productId: "clear using forget-product",
    productIdRemember: "clear using forget-product",
    productPayload: "clear using forget-product",
    productPayloadRemember: "clear using forget-product",
    runtimeContext: "readonly",
    selectedTheme: "readonly",
    showProductSelector: "readonly",
    singleModule: "readonly",
    systemInformation: "readonly",
    systemVersion: "readonly",
    themeSelector: "readonly",
    uiCulture: "readonly",
    useProductSelector: "readonly",
    userId: "readonly",
    useSignalR: "readonly",
    version: "readonly"
};

export default {
    setup: setup,
    get: get,
    set: set,
    userSet: userSet,
    isEnabled: isEnabled,
    list: list,
    reset: reset,
    keys: keys,
    instructions: instructions,
    getUiCulture: () => get(keys.uiCulture) as string,
    getCulture: () => get(keys.culture) as string,
    productionMode: () => get(keys.productionMode) as boolean,
    getUserName: () => get(keys.userName) as string,
    debug: () => get(keys.debug) as boolean,
    ignorePageUnloadDirtyCheck: () => get(keys.ignorePageUnloadDirtyCheck) as boolean
};
